/**
 * Classroom's OAuth callback.
 *
 * Only the registrar's intake completes here (ADR 0037); the teaching loop's
 * popup sign-in is retired (ADR 0026 as amended 2026-10-19). The intake leaves
 * the page for Google; its verifier stays on the server, so this page
 * posts the code and state to the intake completion and returns the browser to
 * the intake page with the outcome.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";

import { ApiError, api } from "@/lib/api/client";

const INTAKE_STATE_PREFIX = "intake.";
const INTAKE_PATH = "/admin/courses/classroom-intake";

function intakeFailure(reason: string): string {
  return `${INTAKE_PATH}?connection=failed&reason=${encodeURIComponent(reason)}`;
}

function errorCode(error: unknown): string {
  if (error instanceof ApiError && typeof error.detail === "object" && error.detail !== null) {
    const code = (error.detail as { code?: unknown }).code;
    if (typeof code === "string" && code) return code;
  }
  return "provider_unavailable";
}

async function completeIntake(state: string, code: string | null, error: string | null): Promise<string> {
  if (error || !code) return intakeFailure(error || "no_code");
  try {
    const result = await api.post<{ return_to?: unknown }>("/classroom-intake/connection/complete", { state, code });
    return typeof result.return_to === "string" && result.return_to.startsWith(`${INTAKE_PATH}?`)
      ? result.return_to
      : `${INTAKE_PATH}?connection=connected`;
  } catch (failure) {
    return intakeFailure(errorCode(failure));
  }
}

export function GoogleClassroomOAuthCallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const query = new URLSearchParams(location.search);
    const state = query.get("state") ?? "";
    if (state.startsWith(INTAKE_STATE_PREFIX)) {
      void completeIntake(state, query.get("code"), query.get("error")).then((to) => navigate(to, { replace: true }));
      return;
    }
  }, [location.search, navigate]);

  // Only the registrar's intake completes here; the teaching sign-in is retired
  // (ADR 0026 as amended 2026-10-19), so any other state has nothing to finish.
  const intake = new URLSearchParams(location.search).get("state")?.startsWith(INTAKE_STATE_PREFIX);
  return <p className="p-6 text-ui text-muted-foreground">{intake ? "Saving the Classroom intake authorization…" : "This sign-in is no longer used. You can close this page."}</p>;
}
